import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useActiveConversation } from '../../contexts/ActiveConversationContext';
import { useConversations } from '../../contexts/ConversationsContext';
import { useMobileDrawer } from '../../contexts/MobileDrawerContext';
import type { ConversationSummary } from '../../types/api';

export function ConversationList() {
  const { t } = useTranslation();
  const { conversations, loading } = useConversations();
  const { runState } = useActiveConversation();
  const { close } = useMobileDrawer();
  const { id: activeId } = useParams();
  const navigate = useNavigate();

  // Lock conversation switching during streaming.
  const isStreaming = runState === 'streaming' || runState === 'sending';

  // Bucket by recency for nicer UI.
  const buckets = bucketByRecency(conversations);

  if (loading && conversations.length === 0) {
    return <div className="sidebar-empty">{t('loading')}</div>;
  }
  if (conversations.length === 0) {
    return <div className="sidebar-empty">{t('noConversations')}</div>;
  }

  const handleSelect = (id: string) => {
    navigate(`/c/${id}`);
    close();
  };

  const renderItem = (c: ConversationSummary) => {
    const isActive = c.id === activeId;
    return (
      <div
        key={c.id}
        className={`chat-item${isActive ? ' active' : ''}${isStreaming && !isActive ? ' disabled' : ''}`}
        onClick={() => {
          if (isStreaming && !isActive) return;
          handleSelect(c.id);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSelect(c.id);
        }}
      >
        {c.title || t('untitled')}
      </div>
    );
  };

  return (
    <div className="sidebar-chats">
      {buckets.today.length > 0 && (
        <>
          <div className="sidebar-section-label">{t('today')}</div>
          {buckets.today.map(renderItem)}
        </>
      )}
      {buckets.yesterday.length > 0 && (
        <>
          <div className="sidebar-section-label">{t('yesterday')}</div>
          {buckets.yesterday.map(renderItem)}
        </>
      )}
      {buckets.earlier.length > 0 && (
        <>
          <div className="sidebar-section-label">{t('earlier')}</div>
          {buckets.earlier.map(renderItem)}
        </>
      )}
    </div>
  );
}

interface Buckets {
  today: ConversationSummary[];
  yesterday: ConversationSummary[];
  earlier: ConversationSummary[];
}

function bucketByRecency(items: ConversationSummary[]): Buckets {
  const today = startOfDay(new Date()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;

  const buckets: Buckets = { today: [], yesterday: [], earlier: [] };
  for (const c of items) {
    const ts = new Date(c.updated_at).getTime();
    if (ts >= today) buckets.today.push(c);
    else if (ts >= yesterday) buckets.yesterday.push(c);
    else buckets.earlier.push(c);
  }
  return buckets;
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}
